import {
  Badge,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { mdiFilter, mdiSort } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Loading from 'components/Loading/Loading';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActivityLogItems,
  selectActivityLogLoading,
} from 'store/activityLog/activityLogSlice';
import { fetchActivityLogsForUserProjectDate } from 'store/activityLog/thunks/fetchActivityLogsForUserProjectDate.thunk';
import ActivityLogRow from './ActivityLogRow';

const DEFAULT_SORT = 'TIME_ASC';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
    // Always use the full available height, even with few rows.
    height: 'calc(100% - 64px)',
    [theme.breakpoints.down('xs')]: {
      ...theme.component.dialogPaperFullscreen,
    },
  },
  dialogActions: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  // Top bar: activity count on the left, filter + sort buttons on the right.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

/**
 * Lists a user's activity logs on a project for a single day. Each row shows two
 * icons (the verb, e.g. eye for "viewed", and the feature, e.g. calendar for the
 * timestamps page) followed by the time and the raw action. A top bar shows the
 * activity count and offers sort + filter (by action) menus. Action text is
 * translated later. Visibility is controlled by the activityLog slice.
 */
const ActivityLogDialog = ({ open, onClose, userId, projectId, date }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const items = useSelector(selectActivityLogItems);
  const loading = useSelector(selectActivityLogLoading);

  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [actionFilters, setActionFilters] = useState([]); // empty = all actions
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);

  useEffect(() => {
    if (open && projectId && userId && date) {
      setActionFilters([]);
      setSortBy(DEFAULT_SORT);
      dispatch(
        fetchActivityLogsForUserProjectDate({ projectId, userId, date })
      );
    }
  }, [dispatch, open, projectId, userId, date]);

  const sortOptions = [
    { value: 'TIME_ASC', label: t('activity_log.sort_oldest', 'Oldest first') },
    {
      value: 'TIME_DESC',
      label: t('activity_log.sort_newest', 'Newest first'),
    },
    {
      value: 'ACTION_ASC',
      label: t('activity_log.sort_action_asc', 'Action A–Z'),
    },
    {
      value: 'ACTION_DESC',
      label: t('activity_log.sort_action_desc', 'Action Z–A'),
    },
  ];

  const distinctActions = useMemo(
    () => [...new Set((items || []).map((item) => item.action))],
    [items]
  );

  const filteredItems = useMemo(() => {
    if (actionFilters.length === 0) {
      return items || [];
    }
    return (items || []).filter((item) => actionFilters.includes(item.action));
  }, [items, actionFilters]);

  const sortedItems = useMemo(() => {
    const next = [...filteredItems];
    next.sort((a, b) => {
      switch (sortBy) {
        case 'TIME_DESC':
          return (b.createdAt || '').localeCompare(a.createdAt || '');
        case 'ACTION_ASC':
          return (a.action || '').localeCompare(b.action || '');
        case 'ACTION_DESC':
          return (b.action || '').localeCompare(a.action || '');
        case 'TIME_ASC':
        default:
          return (a.createdAt || '').localeCompare(b.createdAt || '');
      }
    });
    return next;
  }, [filteredItems, sortBy]);

  const toggleActionFilter = (action) => {
    setActionFilters((prev) =>
      prev.includes(action)
        ? prev.filter((value) => value !== action)
        : [...prev, action]
    );
  };

  return (
    <Dialog
      open={open}
      classes={{ paper: classes.dialogPaper }}
      onClose={onClose}
      aria-labelledby="activity-log-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader
        onClose={onClose}
        title={`${t('activity_log.title', 'Activity log')}${
          date ? ` — ${moment(date).format('l')}` : ''
        }`}
      />
      <DialogContent>
        {loading ? (
          <Loading tiny />
        ) : (
          <>
            {items && items.length > 0 ? (
              <div className={classes.toolbar}>
                <Typography variant="body2" color="textSecondary">
                  {sortedItems.length}{' '}
                  {t('activity_log.activities_label', 'activities')}
                </Typography>
                <div className={classes.toolbarActions}>
                  <Tooltip title={t('activity_log.filter', 'Filter')}>
                    <IconButton
                      size="small"
                      onClick={(event) =>
                        setFilterAnchor(event.currentTarget)
                      }
                      aria-label={t('activity_log.filter', 'Filter')}
                    >
                      <Badge
                        color="secondary"
                        badgeContent={actionFilters.length}
                        invisible={actionFilters.length === 0}
                      >
                        <Icon path={mdiFilter} size={1} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('activity_log.sort', 'Sort')}>
                    <IconButton
                      size="small"
                      onClick={(event) => setSortAnchor(event.currentTarget)}
                      aria-label={t('activity_log.sort', 'Sort')}
                    >
                      <Icon path={mdiSort} size={1} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ) : null}

            <Menu
              anchorEl={filterAnchor}
              open={Boolean(filterAnchor)}
              onClose={() => setFilterAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {distinctActions.map((action) => {
                const checked = actionFilters.includes(action);
                return (
                  <MenuItem
                    key={action}
                    onClick={() => toggleActionFilter(action)}
                    dense
                  >
                    <ListItemIcon style={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText primary={action} />
                  </MenuItem>
                );
              })}
              {actionFilters.length > 0 && [
                <Divider key="divider" />,
                <MenuItem
                  key="clear"
                  onClick={() => setActionFilters([])}
                  dense
                >
                  <ListItemText
                    primary={t('activity_log.clear_filters', 'Clear filters')}
                  />
                </MenuItem>,
              ]}
            </Menu>
            <Menu
              anchorEl={sortAnchor}
              open={Boolean(sortAnchor)}
              onClose={() => setSortAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {sortOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={sortBy === option.value}
                  onClick={() => {
                    setSortBy(option.value);
                    setSortAnchor(null);
                  }}
                  dense
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>

            {sortedItems.length > 0 ? (
              sortedItems.map((item) => (
                <ActivityLogRow
                  key={item.id || `${item.createdAt}-${item.action}`}
                  item={item}
                />
              ))
            ) : (
              <p>{t('activity_log.no_logs', 'No activity logs for this day')}</p>
            )}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="dark">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityLogDialog;
